import type { AccessToken, Session, SessionStatus } from './types.ts'

/**
 * The observable session. A ~40-line store rather than Zustand: this package is consumed
 * by two apps and (later) a native shell, and a state library in here would be a
 * dependency they all inherit for one value.
 *
 * The access token lives in this closure and nowhere else — see the README, "Dove stanno i token".
 */

export type SessionListener = (session: Session) => void

const ANONYMOUS: Session = { status: 'anonymous', accessToken: null, idToken: null }

export interface SessionStore {
  get(): Session
  subscribe(listener: SessionListener): () => void
  setAuthenticating(): void
  setAuthenticated(tokens: { accessToken: AccessToken; idToken: string | null }): void
  reset(): void
  /** The valid access token, or null when absent or within `skewMs` of expiry. */
  validAccessToken(skewMs?: number): string | null
}

/** Refresh this early rather than at the exact expiry, so a request in flight is not cut off. */
export const DEFAULT_EXPIRY_SKEW_MS = 30_000

export function createSessionStore(): SessionStore {
  let session: Session = ANONYMOUS
  const listeners = new Set<SessionListener>()

  function emit(next: Session): void {
    session = next
    // Copied before iterating: a listener that unsubscribes itself would otherwise
    // mutate the set mid-iteration.
    for (const listener of [...listeners]) listener(session)
  }

  return {
    get: () => session,

    subscribe(listener) {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },

    setAuthenticating() {
      emit({ status: 'authenticating', accessToken: null, idToken: null })
    },

    setAuthenticated({ accessToken, idToken }) {
      emit({ status: 'authenticated', accessToken, idToken })
    },

    reset() {
      emit(ANONYMOUS)
    },

    validAccessToken(skewMs = DEFAULT_EXPIRY_SKEW_MS) {
      const token = session.accessToken
      if (!token) return null
      return token.expiresAt - skewMs > Date.now() ? token.value : null
    },
  }
}

/** `expires_in` seconds → an absolute deadline, resolved once at issue time. */
export function toAccessToken(value: string, expiresInSeconds: number): AccessToken {
  return { value, expiresAt: Date.now() + expiresInSeconds * 1000 }
}

export type { Session, SessionStatus }
